import { Assets, Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { ObjectPool } from './pool.js';
import { ParallaxBackdrop, type ParallaxOptions } from './parallax.js';
import { ParticleBudget, type ParticleTier } from './particles.js';

/**
 * View model handed to a scene (tech-design §4). Scenes receive **selector-derived
 * data only** — never `GameState` — and never mutate anything. Magnitudes arrive
 * as `log10` proxies so a 10^40 population maps to a bounded sprite count, never
 * 10^40 sprites (§4 performance rule).
 */
export interface AgeSceneViewModel {
  /** Logic ticks elapsed — the M0 skeleton's clock. */
  readonly ticks: number;
  /** `log10` of the age's primary quantity; drives the proxy sprite count. */
  readonly logMagnitude: number;
}

/** The render surface's logical size in CSS pixels — the mount box, which is the
 * full window on desktop but only the top half on a phone (see app/style.css). */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** Default surface size for scenes created without an explicit viewport (tests). */
const windowViewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/** Effect triggers pushed to the scene out-of-band (breakthrough hits, clicks). */
export type SceneEvent =
  | { readonly type: 'click' }
  | { readonly type: 'purchase' }
  | { readonly type: 'breakthroughHit' };

/**
 * One scene per age, registered by {@link AgeId}. `update` syncs to the latest
 * view model (per logic tick); `animate` advances continuous motion (per render
 * frame, framerate-independent via `dtSeconds`); `handleEvent` fires juice.
 */
export abstract class AgeScene {
  abstract readonly container: Container;
  abstract update(vm: AgeSceneViewModel): void;
  abstract animate(dtSeconds: number): void;
  abstract handleEvent(event: SceneEvent): void;
  abstract setParticleTier(tier: ParticleTier): void;
  abstract destroy(): void;
}

interface Particle {
  readonly gfx: Graphics;
  vx: number;
  vy: number;
  life: number;
  ttl: number;
}

/** How many proxy sprites represent one decade (order of magnitude). */
const SPRITES_PER_DECADE = 12;
const MAX_PROXY_SPRITES = 240;
const PARTICLE_BASE_BUDGET = 400;

/**
 * The placeholder world view wired end-to-end (the real Primordial scene lands
 * with Age 1 / GAM-9). It exercises every foundation piece: a log-scale proxy
 * swarm drawn from an {@link ObjectPool}, a particle burst on events gated by a
 * {@link ParticleBudget}, and a heading driven by the view model.
 */
export class PlaceholderScene extends AgeScene {
  readonly container = new Container();
  private readonly backdrop = new Container();
  private readonly swarm = new Container();
  private readonly burst = new Container();

  private readonly spritePool: ObjectPool<Graphics>;
  private readonly particlePool: ObjectPool<Graphics>;
  private readonly budget = new ParticleBudget(PARTICLE_BASE_BUDGET);
  private readonly active: Particle[] = [];
  private readonly shown: Graphics[] = [];
  private phase = 0;
  private destroyed = false;
  private backdropSprite: Sprite | null = null;
  private parallax: ParallaxBackdrop | null = null;
  private readonly viewport: () => Viewport;
  private readonly onResize = (): void => {
    this.fitBackdrop();
  };

  constructor(options: SceneOptions = {}) {
    super();
    this.viewport = options.viewport ?? windowViewport;

    // The mouse-parallax backdrop stacks above the legacy static render: its base
    // plate is opaque, so once the parallax art exists it takes over the frame, and
    // until then whatever loads underneath still shows. Each missing layer degrades
    // independently inside ParallaxBackdrop.
    if (options.background !== undefined) {
      this.parallax = new ParallaxBackdrop(options.background, this.viewport);
    }

    // Generated world-view render, when it exists. Loading is fire-and-forget:
    // dev builds usually lack /content/ art (fetched from R2 at build time), so a
    // failed load silently keeps the procedural look.
    if (options.backdropUrl !== undefined) {
      const url = options.backdropUrl;
      void Assets.load<Texture>(url)
        .then((texture) => {
          if (this.destroyed) {
            return;
          }
          this.backdropSprite = new Sprite(texture);
          this.backdrop.addChild(this.backdropSprite);
          this.fitBackdrop();
          window.addEventListener('resize', this.onResize);
        })
        .catch(() => {
          // No render available — the procedural scene stands on its own.
        });
    }

    this.spritePool = new ObjectPool<Graphics>(
      () => {
        const g = new Graphics();
        g.circle(0, 0, 6).fill({ color: 0x9ece6a });
        return g;
      },
      {
        reset: (g) => {
          g.visible = false;
        },
        maxIdle: MAX_PROXY_SPRITES,
      },
    );

    this.particlePool = new ObjectPool<Graphics>(
      () => {
        const g = new Graphics();
        g.circle(0, 0, 3).fill({ color: 0xbb9af7 });
        return g;
      },
      {
        reset: (g) => {
          g.visible = false;
          g.alpha = 1;
        },
        maxIdle: PARTICLE_BASE_BUDGET,
      },
    );

    this.container.addChild(this.backdrop, this.swarm, this.burst);
    if (this.parallax) {
      this.container.addChildAt(this.parallax.container, 1);
    }
  }

  /** Cover-fit the backdrop to the window (the renderer resizes to window). */
  private fitBackdrop(): void {
    const sprite = this.backdropSprite;
    if (!sprite) {
      return;
    }
    const { width, height } = sprite.texture;
    if (width <= 0 || height <= 0) {
      return;
    }
    const view = this.viewport();
    const scale = Math.max(view.width / width, view.height / height);
    sprite.scale.set(scale);
    sprite.x = (view.width - width * scale) / 2;
    sprite.y = (view.height - height * scale) / 2;
  }

  update(vm: AgeSceneViewModel): void {
    const want = Math.min(
      MAX_PROXY_SPRITES,
      Math.max(0, Math.round(vm.logMagnitude * SPRITES_PER_DECADE)),
    );
    // Grow / shrink the swarm to match the proxy count (pooled, no per-tick alloc).
    while (this.shown.length < want) {
      const g = this.spritePool.acquire();
      g.visible = true;
      const index = this.shown.length;
      // Deterministic phyllotaxis-ish spiral so growth reads as accumulation.
      const angle = index * 2.399963;
      const radius = 14 + Math.sqrt(index) * 16;
      g.x = 180 + Math.cos(angle) * radius;
      g.y = 200 + Math.sin(angle) * radius;
      this.swarm.addChild(g);
      this.shown.push(g);
    }
    while (this.shown.length > want) {
      const g = this.shown.pop();
      if (g) {
        this.swarm.removeChild(g);
        this.spritePool.release(g);
      }
    }
  }

  handleEvent(event: SceneEvent): void {
    const desired = event.type === 'breakthroughHit' ? 64 : 16;
    const granted = this.budget.requestBurst(desired);
    for (let i = 0; i < granted; i++) {
      const gfx = this.particlePool.acquire();
      gfx.visible = true;
      gfx.x = 180;
      gfx.y = 200;
      const angle = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 180;
      this.burst.addChild(gfx);
      this.active.push({
        gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        ttl: 0.6 + Math.random() * 0.4,
      });
    }
  }

  animate(dtSeconds: number): void {
    if (dtSeconds <= 0) {
      return;
    }
    this.phase += dtSeconds;
    this.parallax?.animate(dtSeconds);
    // Gentle breathing on the swarm so a static economy still feels alive.
    this.swarm.scale.set(1 + Math.sin(this.phase * 1.5) * 0.02);

    let expired = 0;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      if (!p) {
        continue;
      }
      p.life += dtSeconds;
      const remaining = 1 - p.life / p.ttl;
      if (remaining <= 0) {
        this.burst.removeChild(p.gfx);
        this.particlePool.release(p.gfx);
        this.active.splice(i, 1);
        expired++;
        continue;
      }
      p.gfx.x += p.vx * dtSeconds;
      p.gfx.y += p.vy * dtSeconds;
      p.gfx.alpha = remaining;
    }
    if (expired > 0) {
      this.budget.release(expired);
    }
  }

  setParticleTier(tier: ParticleTier): void {
    this.budget.setTier(tier);
  }

  destroy(): void {
    this.destroyed = true;
    window.removeEventListener('resize', this.onResize);
    this.parallax?.destroy();
    this.parallax = null;
    this.active.length = 0;
    this.shown.length = 0;
    this.spritePool.clear();
    this.particlePool.clear();
    this.container.destroy({ children: true });
  }
}

/** Per-scene wiring supplied by the app (art URLs and the like). */
export interface SceneOptions {
  /** Generated full-scene render drawn behind the procedural layer. */
  readonly backdropUrl?: string | undefined;
  /** The age's mouse-parallax backdrop (content-schema §2 `background`), with
   * asset ids already resolved to URLs by the app. */
  readonly background?: ParallaxOptions | undefined;
  /** Live render-surface size; defaults to the window when the app doesn't inject
   * one (e.g. unit tests). The renderer wires this to the Pixi surface so scenes
   * compose inside the canvas box, not the whole window. */
  readonly viewport?: (() => Viewport) | undefined;
}

/** Registry of scenes keyed by age. New ages register their scene here. */
export type AgeId = 'placeholder';

const SCENE_REGISTRY: Record<AgeId, (options: SceneOptions) => AgeScene> = {
  placeholder: (options) => new PlaceholderScene(options),
};

export function createScene(id: AgeId, options: SceneOptions = {}): AgeScene {
  return SCENE_REGISTRY[id](options);
}
